import type { AddinOrganisation } from './addin-organisation.model';

/** Same key Trace uses for the organisation the user is operating in. */
export const ACTIVE_ORGANISATION_CODE_STORAGE_KEY = 'activeOrganisationCode';

/**
 * Accessible organisations for the Excel add-in session.
 * The selected code is sent as `x-organization-code` and used when refreshing tokens.
 */
export class AddinOrganisationContextStore {
    private available: AddinOrganisation[] = [];
    private selected: AddinOrganisation | null = null;
    private loaded = false;

    availableOrganisations(): readonly AddinOrganisation[] {
        return this.available;
    }

    selectedOrganisation(): AddinOrganisation | null {
        return this.selected;
    }

    isLoaded(): boolean {
        return this.loaded;
    }

    /** Broker / multi-org: more than one organisation can be chosen without signing in again. */
    canSwitchOrganisation(): boolean {
        return this.available.length > 1;
    }

    selectedOrganisationCode(): string {
        return this.selected?.organisationCode.trim() ?? '';
    }

    /** Last chosen organisation, including before the accessible list is loaded again. */
    storedOrganisationCode(): string {
        return this.readPersistedCode();
    }

    setAvailableOrganisations(organisations: readonly AddinOrganisation[]): void {
        this.available = [...organisations];
        this.loaded = true;
        this.restoreSelection(this.available);
    }

    selectOrganisation(organisation: AddinOrganisation): void {
        const organisationCode = organisation.organisationCode.trim();
        if (!organisationCode) {
            return;
        }
        const match = this.findByCode(this.available, organisationCode);
        if (!match) {
            return;
        }
        this.selected = match;
        this.persistCode(organisationCode);
    }

    /** Drops the accessible list and the selected organisation (sign-out or a new login). */
    clearSessionSelection(): void {
        this.available = [];
        this.selected = null;
        this.loaded = false;
        this.removePersistedCode();
    }

    private restoreSelection(organisations: readonly AddinOrganisation[]): void {
        const storedCode = this.readPersistedCode();
        if (!storedCode) {
            this.selected = null;
            return;
        }
        const match = this.findByCode(organisations, storedCode);
        if (match) {
            this.selected = match;
            return;
        }
        this.selected = null;
        this.removePersistedCode();
    }

    private findByCode(
        organisations: readonly AddinOrganisation[],
        code: string
    ): AddinOrganisation | undefined {
        const trimmed = code.trim();
        return organisations.find(
            (organisation) => organisation.organisationCode.trim() === trimmed
        );
    }

    private readPersistedCode(): string {
        try {
            return sessionStorage.getItem(ACTIVE_ORGANISATION_CODE_STORAGE_KEY)?.trim() ?? '';
        } catch {
            return '';
        }
    }

    private persistCode(organisationCode: string): void {
        try {
            sessionStorage.setItem(ACTIVE_ORGANISATION_CODE_STORAGE_KEY, organisationCode);
        } catch {
            /* ignore quota / private mode */
        }
    }

    private removePersistedCode(): void {
        try {
            sessionStorage.removeItem(ACTIVE_ORGANISATION_CODE_STORAGE_KEY);
        } catch {
            /* ignore */
        }
    }
}

/** Shared instance for the task pane. */
export const addinOrganisationContext = new AddinOrganisationContextStore();
